import React, { useEffect, useMemo, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { canAccessAdminPath, getAdmin, ROLE_LABELS } from "../../lib/auth";
import {
  Activity,
  BarChart3,
  BookOpen,
  Building2,
  CalendarDays,
  ChevronRight,
  ClipboardCheck,
  DatabaseBackup,
  FileText,
  Gauge,
  Grid3X3,
  Image,
  LayoutDashboard,
  LifeBuoy,
  ListChecks,
  LockKeyhole,
  LogOut,
  MapPinned,
  Menu,
  Newspaper,
  PanelLeftClose,
  PanelLeftOpen,
  Search,
  ShieldCheck,
  Sparkles,
  Users,
  X,
} from 'lucide-react';

const navGroups = [
  {
    title: 'Control Center',
    items: [
      { to: '/adxm/dashboard', label: 'Dashboard', icon: LayoutDashboard, keywords: 'overview home summary' },
      { to: '/adxm/ui-ux', label: 'UI/UX Center', icon: Sparkles, keywords: 'design responsive audit' },
      { to: '/adxm/analytics', label: 'Analytics', icon: BarChart3, keywords: 'traffic page views visitors' },
    ],
  },
  {
    title: 'Content Publishing',
    items: [
      { to: '/adxm/news', label: 'News CMS', icon: Newspaper, keywords: 'posts articles updates' },
      { to: '/adxm/events', label: 'Events CMS', icon: CalendarDays, keywords: 'calendar activities' },
      { to: '/adxm/notices', label: 'Notice Board', icon: FileText, keywords: 'urgent notices announcements' },
      { to: '/adxm/content-approvals', label: 'Content Approval', icon: ClipboardCheck, keywords: 'workflow review publish' },
    ],
  },
  {
    title: 'Institutional Pages',
    items: [
      { to: '/adxm/institutional-pages', label: 'All Pages', icon: Building2, keywords: 'departments centers directorates units' },
      { to: '/adxm/departments', label: 'Departments', icon: BookOpen, keywords: 'academic hod programmes' },
      { to: '/adxm/centers', label: 'Centres', icon: Grid3X3, keywords: 'centers centres training' },
      { to: '/adxm/directorates', label: 'Directorates', icon: Gauge, keywords: 'director services mandate' },
      { to: '/adxm/units', label: 'Units', icon: ListChecks, keywords: 'unit functions offices' },
    ],
  },
  {
    title: 'Media & Resources',
    items: [
      { to: '/adxm/downloads', label: 'Downloads', icon: FileText, keywords: 'documents files pdf' },
      { to: '/adxm/staff', label: 'Staff', icon: Users, keywords: 'profiles directory' },
      { to: '/adxm/campus-locations', label: 'Campus Map', icon: MapPinned, keywords: 'locations map tour' },
      { to: '/adxm/gallery-albums', label: 'Gallery Albums', icon: Image, keywords: 'albums media images' },
      { to: '/adxm/gallery-media', label: 'Gallery Media', icon: Image, keywords: 'photos videos virtual tour' },
      { to: '/adxm/journal-issues', label: 'Journal Issues', icon: BookOpen, keywords: 'research journal issues' },
      { to: '/adxm/journal-articles', label: 'Journal Articles', icon: FileText, keywords: 'papers publications' },
    ],
  },
  {
    title: 'Engagement',
    items: [
      { to: '/adxm/alumni', label: 'Alumni Stories', icon: Users, keywords: 'success stories graduates' },
      { to: '/adxm/partnerships', label: 'Partnerships', icon: Building2, keywords: 'partners mou collaboration' },
      { to: '/adxm/projects', label: 'TETFund Projects', icon: Activity, keywords: 'tetfund projects progress intervention tracker' },
      { to: '/adxm/support-tickets', label: 'Support Tickets', icon: LifeBuoy, keywords: 'help desk requests' },
      { to: '/adxm/search-insights', label: 'Search Insights', icon: Search, keywords: 'queries popular search' },
      { to: '/adxm/navigation', label: 'Mega Menu', icon: Menu, keywords: 'navigation menu links' },
    ],
  },
  {
    title: 'Security & System',
    items: [
      { to: '/adxm/seo', label: 'SEO Manager', icon: Sparkles, keywords: 'metadata google sitemap' },
      { to: '/adxm/security', label: 'Security Center', icon: ShieldCheck, keywords: 'protection login status' },
      { to: '/adxm/audit-logs', label: 'Activity Logs', icon: Activity, keywords: 'audit admin actions' },
      { to: '/adxm/backups', label: 'Backup Center', icon: DatabaseBackup, keywords: 'restore export' },
      { to: '/adxm/admin-users', label: 'Admin Users', icon: LockKeyhole, keywords: 'roles users access' },
    ],
  },
];

function getAdminMeta() {
  const admin = getAdmin();
  return {
    name: admin?.name || admin?.full_name || admin?.username || "Administrator",
    role: admin?.role || "",
    roleLabel: admin?.role_label || ROLE_LABELS[admin?.role] || "Secure CMS session",
  };
}

function NavSection({ group, collapsed, query, onNavigate }) {
  const filteredItems = group.items.filter((item) => {
    const haystack = `${item.label} ${item.keywords || ''}`.toLowerCase();
    return haystack.includes(query.toLowerCase()) && canAccessAdminPath(item.to);
  });

  if (!filteredItems.length) return null;

  return (
    <div className="fpb-admin-nav-section">
      {!collapsed && <p className="fpb-admin-nav-section-title">{group.title}</p>}
      <div className="space-y-1.5">
        {filteredItems.map((item) => {
          const Icon = item.icon || FileText;
          return (
            <NavLink
              key={item.to}
              to={item.to}
              onClick={onNavigate}
              title={collapsed ? item.label : undefined}
              className={({ isActive }) =>
                `fpb-admin-nav-link ${isActive ? 'fpb-admin-nav-link-active' : ''}`
              }
            >
              <span className="fpb-admin-nav-icon" aria-hidden="true"><Icon size={18} strokeWidth={2.2} /></span>
              {!collapsed && <span className="fpb-admin-nav-label">{item.label}</span>}
              {!collapsed && <ChevronRight className="fpb-admin-nav-arrow" size={15} aria-hidden="true" />}
            </NavLink>
          );
        })}
      </div>
    </div>
  );
}

function AdminSidebar({ collapsed = false, onToggleCollapse, onNavigate, logout, mobile = false }) {
  const [query, setQuery] = useState('');
  const adminMeta = useMemo(() => getAdminMeta(), []);

  return (
    <div className="fpb-admin-sidebar-inner" data-collapsed={collapsed ? 'true' : 'false'}>
      <div className="fpb-admin-brand-wrap">
        <Link to="/adxm/dashboard" onClick={onNavigate} className="fpb-admin-brand">
          <img src="/fpb.png" alt="Federal Polytechnic Bida" className="fpb-admin-brand-logo" />
          {!collapsed && (
            <span className="min-w-0">
              <span className="fpb-admin-brand-kicker">Federal Polytechnic Bida</span>
              <strong className="fpb-admin-brand-title">Admin Command</strong>
            </span>
          )}
        </Link>

        {!mobile && (
          <button
            type="button"
            onClick={onToggleCollapse}
            className="fpb-admin-collapse-btn"
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {collapsed ? <PanelLeftOpen size={18} /> : <PanelLeftClose size={18} />}
          </button>
        )}
      </div>

      {!collapsed && (
        <div className="fpb-admin-user-card">
          <div className="fpb-admin-user-avatar">{adminMeta.name.slice(0, 1).toUpperCase()}</div>
          <div className="min-w-0">
            <p className="truncate text-sm font-black text-slate-950">{adminMeta.name}</p>
            <p className="truncate text-xs font-bold text-emerald-700">{adminMeta.roleLabel}</p>
          </div>
        </div>
      )}

      {!collapsed && (
        <label className="fpb-admin-nav-search">
          <Search size={16} aria-hidden="true" />
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search menu..."
            aria-label="Search admin menu"
          />
        </label>
      )}

      <nav className="fpb-admin-sidebar-scroll" aria-label="Admin navigation">
        {navGroups.map((group) => (
          <NavSection
            key={group.title}
            group={group}
            collapsed={collapsed}
            query={query}
            onNavigate={onNavigate}
          />
        ))}
      </nav>

      <div className="fpb-admin-sidebar-footer">
        {!collapsed && (
          <Link to="/" className="fpb-admin-view-site" target="_blank" rel="noreferrer">
            View public website
          </Link>
        )}
        <button onClick={logout} className="fpb-admin-logout" title="Logout">
          <LogOut size={18} />
          {!collapsed && <span>Logout</span>}
        </button>
      </div>
    </div>
  );
}

export default function AdminLayout({ title, subtitle, actions, children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem('fpb_admin_sidebar_collapsed') === 'true');
  const [online, setOnline] = useState(typeof navigator === 'undefined' ? true : navigator.onLine);

  useEffect(() => {
    document.body.classList.add('fpb-admin-route');
    return () => document.body.classList.remove('fpb-admin-route');
  }, []);

  useEffect(() => {
    localStorage.setItem('fpb_admin_sidebar_collapsed', collapsed ? 'true' : 'false');
  }, [collapsed]);

  useEffect(() => {
    document.body.classList.toggle('fpb-no-scroll', mobileOpen);
    return () => document.body.classList.remove('fpb-no-scroll');
  }, [mobileOpen]);

  useEffect(() => {
    const setOnlineStatus = () => setOnline(navigator.onLine);
    window.addEventListener('online', setOnlineStatus);
    window.addEventListener('offline', setOnlineStatus);
    return () => {
      window.removeEventListener('online', setOnlineStatus);
      window.removeEventListener('offline', setOnlineStatus);
    };
  }, []);

  function logout() {
    localStorage.removeItem('fpb_admin_token');
    localStorage.removeItem('fpb_admin');
    localStorage.removeItem('adminToken');
    localStorage.removeItem('admin_token');
    navigate('/adxm/login');
  }

  const breadcrumb = useMemo(() => {
    const parts = location.pathname.split('/').filter(Boolean).slice(1);
    if (!parts.length) return 'Dashboard';
    return parts.map((part) => part.replace(/-/g, ' ')).join(' / ');
  }, [location.pathname]);

  return (
    <div className="fpb-admin-shell min-h-screen text-slate-900" data-sidebar-collapsed={collapsed ? 'true' : 'false'}>
      <div className="fpb-admin-mobile-topbar">
        <button
          type="button"
          onClick={() => setMobileOpen(true)}
          className="fpb-admin-mobile-menu-btn"
          aria-label="Open admin menu"
        >
          <Menu size={20} />
          <span>Menu</span>
        </button>
        <Link to="/adxm/dashboard" className="min-w-0 text-right">
          <div className="truncate text-xs font-black uppercase tracking-[0.2em] text-emerald-700">FPB Admin</div>
          <div className="truncate text-sm font-black text-slate-950">Command Center</div>
        </Link>
      </div>

      <div className="fpb-admin-mobile-drawer" data-open={mobileOpen ? 'true' : 'false'} aria-hidden={!mobileOpen}>
        <button
          type="button"
          className="fpb-admin-mobile-backdrop"
          onClick={() => setMobileOpen(false)}
          aria-label="Close admin menu"
        />
        <aside className="fpb-admin-mobile-panel" aria-label="Mobile admin sidebar">
          <div className="mb-4 flex justify-end">
            <button
              type="button"
              onClick={() => setMobileOpen(false)}
              className="fpb-admin-mobile-close"
              aria-label="Close admin menu"
            >
              <X size={20} />
            </button>
          </div>
          <AdminSidebar mobile onNavigate={() => setMobileOpen(false)} logout={logout} />
        </aside>
      </div>

      <aside className="fpb-admin-desktop-sidebar" aria-label="Desktop admin sidebar">
        <AdminSidebar
          collapsed={collapsed}
          onToggleCollapse={() => setCollapsed((value) => !value)}
          logout={logout}
        />
      </aside>

      <main className="fpb-admin-main">
        <header className="fpb-admin-page-header">
          <div className="fpb-admin-header-main">
            <div className="min-w-0">
              <p className="fpb-admin-breadcrumb">Secure CMS / {breadcrumb}</p>
              <h2 className="fpb-admin-page-title">{title}</h2>
              {subtitle && <p className="fpb-admin-page-subtitle">{subtitle}</p>}
            </div>
            <div className="fpb-admin-header-actions">
              <div className={`fpb-admin-status-pill ${online ? 'is-online' : 'is-offline'}`}>
                <span /> {online ? 'Online' : 'Offline'}
              </div>
              <Link to="/" target="_blank" rel="noreferrer" className="fpb-admin-ghost-link">Preview Site</Link>
              {actions && <div className="fpb-admin-actions-wrap">{actions}</div>}
            </div>
          </div>
        </header>

        <section className="fpb-admin-content-area">{children}</section>
      </main>
    </div>
  );
}
